import { useRoutes, Navigate } from 'react-router'
import { lazy, Suspense, useEffect, type ReactNode } from 'react'
// components
import { LoadingScreen } from 'shared/ui/loading-screen'
import { useNotFoundRedirect } from 'shared/hooks/useNotFoundRedirect'
// hooks
import { usePageTitle } from 'shared/hooks/usePageTitle'
// config
// import { PATH_AFTER_LOGIN } from 'config-global';
//
import { termsOfUseRoutes } from './terms-of-use'
import { privacyPolicyRoutes } from './privacy-policy'
import { useTranslation } from 'shared/i18n'
import { legacyNewUiRedirectPaths } from '../legacyNewUiRedirectPaths'

function PageTitle ({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  usePageTitle(title)
  return <>{children}</>
}

function NewUiRoute ({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <Suspense fallback={<LoadingScreen sx={{ minHeight: '100vh' }} />}>
      <PageTitle title={title}>{children}</PageTitle>
    </Suspense>
  )
}

// アクセスできないパス(存在しない・権限が無い)はトーストを出して映像一覧(/)へ戻す
function NotFoundRedirect (): JSX.Element {
  const redirect = useNotFoundRedirect()
  useEffect(() => {
    redirect()
  }, [redirect])
  return <></>
}

// TEST NEW UI
const StreamView = lazy(async () => await import('features/stream'))
const UserView = lazy(async () => await import('features/user'))
const AreaView = lazy(async () => await import('features/area'))
const MapManagementView = lazy(async () => await import('features/map/MapManagementView'))
const MapEditView = lazy(async () => await import('features/map/MapEditView'))
const TagView = lazy(async () => await import('features/tag'))
const DeviceView = lazy(async () => await import('features/device'))
const RecordingView = lazy(async () => await import('features/recording'))
const ClipView = lazy(async () => await import('features/clip'))
const AccountSettingsView = lazy(async () => await import('features/account-settings'))
const CompanySettingsView = lazy(async () => await import('features/company-settings'))
const FirmwareView = lazy(async () => await import('features/firmware'))
const PatliteView = lazy(async () => await import('features/patlite'))
const AiDetectionListView = lazy(async () => await import('features/ai/AiDetectionListView'))
const AiDetectionDetailView = lazy(async () => await import('features/ai/AiDetectionDetailView'))
const HandsLicensesView = lazy(async () => await import('features/hands-license/HandsLicensesView'))
const ViewerView = lazy(async () => await import('features/viewer'))
const LoginView = lazy(async () => await import('features/auth/ui/LoginView'))
const ForgotPasswordView = lazy(async () => await import('features/auth/ui/ForgotPasswordView'))
const SamlLoginView = lazy(async () => await import('features/auth/ui/SamlLoginView'))
// ----------------------------------------------------------------------

// 正規ドメインへ飛ばす対象の認証画面
const CANONICAL_DOMAIN_AUTH_PATHS = ['/login', '/forgot-password', '/new-password']

/**
 * 正規ドメイン外で認証画面を開いたときの遷移先を返す。遷移しない場合は null。
 * テナントのサブドメイン以外では Cookie のドメインが合わず、ログインしても保持されないため。
 */
function getCanonicalDomainAuthUrl (): string | null {
  if (['local'].includes(process.env.APP_ENV ?? '')) return null
  const { hostname, pathname } = new URL(window.location.href)
  if (hostname.endsWith(`.${process.env.JIZAIPAD_DOMAIN}`) || hostname === process.env.JIZAIPAD_DOMAIN) return null
  if (!CANONICAL_DOMAIN_AUTH_PATHS.includes(pathname)) return null
  return `https://${process.env.JIZAIPAD_DOMAIN}${pathname}`
}

export default function Router () {
  const { t } = useTranslation()
  const canonicalDomainAuthUrl = getCanonicalDomainAuthUrl()

  // 描画中に window.location を書き換えないよう、遷移は commit 後の effect で行う
  useEffect(() => {
    if (canonicalDomainAuthUrl != null) {
      window.location.href = canonicalDomainAuthUrl
    }
  }, [canonicalDomainAuthUrl])

  // 遷移する場合は画面を何も描画しない
  return useRoutes(canonicalDomainAuthUrl != null ? [] : [
    // 新UI (レイアウトなし)
    {
      path: '/',
      element: (
        <NewUiRoute title={t('app.sections.title.stream')}><StreamView /></NewUiRoute>
      )
    },
    {
      path: 'devices',
      element: (
        <NewUiRoute title={t('app.sections.title.device')}><DeviceView /></NewUiRoute>
      )
    },
    {
      path: 'users',
      element: (
        <NewUiRoute title={t('app.sections.title.user')}><UserView /></NewUiRoute>
      )
    },
    {
      path: 'area',
      element: (
        <NewUiRoute title={t('app.sections.title.area')}><AreaView /></NewUiRoute>
      )
    },
    {
      path: 'maps',
      element: (
        <NewUiRoute title={t('app.sections.title2')}><MapManagementView /></NewUiRoute>
      )
    },
    {
      path: 'maps/new',
      element: (
        <NewUiRoute title={t('app.sections.title3')}><MapEditView /></NewUiRoute>
      )
    },
    {
      path: 'maps/:siteId/edit',
      element: (
        <NewUiRoute title={t('app.sections.title4')}><MapEditView /></NewUiRoute>
      )
    },
    {
      path: 'tags',
      element: (
        <NewUiRoute title={t('app.sections.title.tag')}><TagView /></NewUiRoute>
      )
    },
    {
      path: 'recording-view',
      element: (
        <NewUiRoute title={t('app.sections.title.recording')}><RecordingView /></NewUiRoute>
      )
    },
    {
      path: 'clips',
      element: (
        <NewUiRoute title={t('app.sections.title.clip')}><ClipView /></NewUiRoute>
      )
    },
    {
      path: 'settings',
      element: (
        <NewUiRoute title={t('app.sections.title.accountSettings')}><AccountSettingsView /></NewUiRoute>
      )
    },
    {
      path: 'company',
      element: (
        <NewUiRoute title={t('app.sections.title.companySettings')}><CompanySettingsView /></NewUiRoute>
      )
    },
    ...legacyNewUiRedirectPaths.map(({ path, to }) => ({
      path,
      element: <Navigate replace to={to} />
    })),
    {
      path: 'firmwares',
      element: (
        <NewUiRoute title={t('app.sections.title.firmware')}><FirmwareView /></NewUiRoute>
      )
    },
    {
      path: 'patlamps',
      element: (
        <NewUiRoute title={t('app.sections.title.patlite')}><PatliteView /></NewUiRoute>
      )
    },
    {
      path: 'ai-detections',
      element: (
        <NewUiRoute title="Jizai Concierge"><AiDetectionListView /></NewUiRoute>
      )
    },
    {
      path: 'ai-detections/:aiDetectionId',
      element: (
        <NewUiRoute title="Jizai Concierge"><AiDetectionDetailView /></NewUiRoute>
      )
    },
    {
      path: 'hands-licenses',
      element: (
        <NewUiRoute title={t('app.sections.title.handsLicense')}><HandsLicensesView /></NewUiRoute>
      )
    },
    {
      path: 'camera/:cameraId',
      element: (
        <NewUiRoute title={t('app.sections.title.viewer')}><ViewerView /></NewUiRoute>
      )
    },
    // SSO ログイン画面: 自前 SamlLoginPageLayout を持つため MainLayout 外で登録
    {
      path: 'sso-login',
      element: (
        <NewUiRoute title={t('app.sections.title.samlLogin')}><SamlLoginView /></NewUiRoute>
      )
    },
    {
      path: 'login',
      element: (
        <NewUiRoute title={t('app.sections.title.login')}><LoginView /></NewUiRoute>
      )
    },
    {
      path: 'forgot-password',
      element: (
        <NewUiRoute title={t('app.sections.title.forgotPassword')}><ForgotPasswordView /></NewUiRoute>
      )
    },
    // 再設定メールのリンク先 `/new-password?email=<email>`。
    // ForgotPasswordView が email prefill 済みの reset ステップから開始する。
    {
      path: 'new-password',
      element: (
        <NewUiRoute title={t('app.sections.title.resetPassword')}><ForgotPasswordView /></NewUiRoute>
      )
    },
    ...termsOfUseRoutes,
    ...privacyPolicyRoutes,
    // 存在しない・権限の無いパスはトーストを出して映像一覧(/)へ戻す
    { path: '*', element: <NotFoundRedirect /> }
  ])
}
